import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { DashboardShell } from "@/components/DashboardShell";
import { CUSTOMER_NAV, PARTNER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { addTicketMessage, getTicket, listTicketMessages, updateTicketStatus } from "@/lib/support";
import { ReplyBox, TicketHeaderCard, TicketHistory } from "@/components/support/TicketPieces";

export const Route = createFileRoute("/_authenticated/support/$ticketId")({
  head: () => ({
    meta: [
      { title: "Ticket Details · Landoo Support" },
      {
        name: "description",
        content: "View your Landoo support ticket history and reply to our support team.",
      },
      { property: "og:title", content: "Ticket Details · Landoo Support" },
      {
        property: "og:description",
        content: "View your Landoo support ticket history and reply to our support team.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: TicketDetailPage,
  errorComponent: TicketError,
  notFoundComponent: TicketMissing,
});

function TicketError({ reset }: { reset: () => void }) {
  const router = useRouter();
  return (
    <div className="p-8 text-center text-sm">
      <p className="text-brown-dark">We couldn’t load this ticket.</p>
      <Button
        size="sm"
        className="mt-3"
        onClick={() => {
          router.invalidate();
          reset();
        }}
      >
        Try again
      </Button>
    </div>
  );
}

function TicketMissing() {
  return (
    <div className="p-8 text-center text-sm text-muted-foreground">
      Ticket not found.{" "}
      <Link to="/support" className="underline">
        Back to Support
      </Link>
    </div>
  );
}

function TicketDetailPage() {
  const { ticketId } = Route.useParams();
  const { user, profile, roles } = useAuth();
  const isPartner = roles.includes("partner");
  const qc = useQueryClient();

  const { data: ticket, isLoading } = useQuery({
    queryKey: ["support", "ticket", ticketId],
    queryFn: () => getTicket(ticketId),
  });
  const { data: messages = [] } = useQuery({
    queryKey: ["support", "messages", ticketId],
    queryFn: () => listTicketMessages(ticketId),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["support", "ticket", ticketId] });
    qc.invalidateQueries({ queryKey: ["support", "messages", ticketId] });
    qc.invalidateQueries({ queryKey: ["support", "mine"] });
  };

  const closed = ticket?.status === "closed";

  const send = async (body: string, file: File | null) => {
    try {
      await addTicketMessage({
        ticketId,
        authorId: user!.id,
        authorName: profile?.full_name ?? "You",
        authorRole: isPartner ? "partner" : "customer",
        body,
        file,
      });
      if (ticket && ticket.status === "waiting_for_user") {
        await updateTicketStatus(ticketId, "in_progress");
      }
      toast.success("Reply sent");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send reply");
    }
  };

  const closeTicket = async () => {
    try {
      await updateTicketStatus(ticketId, "closed");
      toast.success("Ticket closed");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not close ticket");
    }
  };

  return (
    <DashboardShell
      title="Ticket Details"
      role={isPartner ? "Partner" : "Customer"}
      items={isPartner ? PARTNER_NAV : CUSTOMER_NAV}
    >
      <Link to="/support" className="text-xs font-semibold text-brown-dark underline">
        ← Back to Support
      </Link>

      {isLoading ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading ticket…</p>
      ) : !ticket ? (
        <p className="mt-4 rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          This ticket is not available.
        </p>
      ) : (
        <div className="mt-4 space-y-4">
          <TicketHeaderCard ticket={ticket} />

          <div>
            <h3 className="mb-2 text-sm font-semibold text-brown-dark">Ticket history</h3>
            <TicketHistory messages={messages} />
          </div>

          {closed ? (
            <p className="rounded-xl bg-secondary/60 p-4 text-sm text-brown-dark">
              This ticket is closed. Create a new ticket if you still need help.
            </p>
          ) : (
            <>
              <ReplyBox onSend={send} />
              <Button variant="outline" size="sm" onClick={closeTicket}>
                Close this ticket
              </Button>
            </>
          )}
        </div>
      )}
    </DashboardShell>
  );
}
